// /context/AuthContext.js

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from '../services/axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

function getSelectedBlogId() {
  try {
    const selected = JSON.parse(localStorage.getItem('selected_blog'));
    return selected?.id || null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const [user, setUser] = useState(null);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // NEW: blog-scoped authz
  const [blogRole, setBlogRole] = useState(null);
  const [blogPerms, setBlogPerms] = useState([]);
  const [platformOverride, setPlatformOverride] = useState(false);

  const navigate = useNavigate();

  const clearAuthState = () => {
    setIsAuthenticated(false);
    setUser(null);
    setIsPlatformAdmin(false);
    setBlogRole(null);
    setBlogPerms([]);
    setPlatformOverride(false);
  };

  const loadBlogAuthz = async () => {
    const blogId = getSelectedBlogId();
    if (!blogId) {
      // No blog selected => no blog-scoped perms
      setBlogRole(null);
      setBlogPerms([]);
      setPlatformOverride(false);
      return;
    }

    try {
      const res = await axios.get('/api/blogs/me/');
      setBlogRole(res.data.role || null);
      setBlogPerms(res.data.perms || []);
      setPlatformOverride(Boolean(res.data.platform_override));
    } catch (err) {
      console.warn('Blog authz load failed:', err);
      // Don’t log the user out here; they may just not have access to this blog.
      setBlogRole(null);
      setBlogPerms([]);
      setPlatformOverride(false);
    }
  };

  const checkAuth = async () => {
    try {
      const res = await axios.get('/api/check-auth/');
      const userData = res.data.user;

      setUser(userData);
      const isSuper =
        (userData?.is_superuser === true) ||
        (res.data?.is_superuser === true) ||
        (res.data?.isSuperUser === true);

      setIsPlatformAdmin(isSuper);

      setIsAuthenticated(true);

      // NEW: after we know we’re authenticated, load blog perms (if blog selected)
      await loadBlogAuthz();
    } catch (err) {
      console.warn('Auth check failed:', err);
      clearAuthState();
    } finally {
      setAuthChecked(true);
    }
  };

  const login = async (username, password) => {
    try {
      localStorage.removeItem('access_token');
      delete axios.defaults.headers.common['Authorization'];

      const res = await axios.post('/api/login/', { username, password });
      if (res.status === 200) {
        const token = res.data.token;

        localStorage.setItem('access_token', token);
        axios.defaults.headers.common['Authorization'] = `Token ${token}`;

        await checkAuth();
        return { success: true };
      }
    } catch (err) {
      console.error('Login failed:', err);
    }
    return { success: false };
  };

  const logout = () => {
    ['access_token', 'authToken', 'selected_blog'].forEach((key) =>
      localStorage.removeItem(key)
    );
    delete axios.defaults.headers.common['Authorization'];
    clearAuthState();
    setAuthChecked(true);
    navigate('/');
  };

  // NEW: helper your blog switcher can call after changing selected_blog
  const refreshBlogAuthz = async () => {
    if (!isAuthenticated) return;
    await loadBlogAuthz();
  };

  // NEW: capability helper
  const can = useMemo(() => {
    return (perm) => platformOverride || blogPerms.includes(perm);
  }, [platformOverride, blogPerms]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      clearAuthState();
      setAuthChecked(true);
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Token ${token}`;
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NEW: if selected_blog changes in another tab or via code that uses localStorage,
  // update permissions.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'selected_blog') {
        refreshBlogAuthz();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        authChecked,

        user,
        isPlatformAdmin,

        // NEW
        blogRole,
        blogPerms,
        platformOverride,
        can,
        refreshBlogAuthz,

        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
